import React, { useState, useRef, useEffect, useCallback, useLayoutEffect, useMemo, Activity } from 'react';
import WaveSurfer from 'wavesurfer.js';
import hotkeys from 'hotkeys-js';
import { ulid } from 'ulidx';
import QuickActionsPanel from './QuickActionsPanel';
import { storeSet, useStoreValue } from '../../store';
import { computeOverlaps, getTrackVolumeAtTime } from '../../helpers/cuesheetMigration';
import { historyStore } from '../../store/historyStore';
import { secondsToTimecode } from '../../helpers/timecodeUtils';
import { getExternalDragState, subscribeExternalDrag, endExternalDrag } from '../../store/externalDragStore';
import FileuploadDropZone from '../FileuploadDropZone';
import { __ } from '@wordpress/i18n';

const LAYER_HEIGHT = 65;
const COMMENT_SECTION_HEIGHT = 20;
const FIRST_TRACK_PADDING = 2;
const TIMELINE_PADDING = 8;
const TRACKS_SECTION_OFFSET = 60; // Offset from top of timeline to start of tracks section
const FPS = 24; // Changed from 30
const BASE_PIXELS_PER_FRAME = 1;
const BASE_SNAP_FRAMES = 12; // 12 frames at 24fps = 0.5 seconds
const MIN_DURATION = 2; // seconds - minimum track duration
const ADSORPTION_DISTANCE = 10; // pixels - snap within 10px of playhead
const DRAG_THRESHOLD = 5; // pixels - minimum mouse movement to count as a drag

// Cut snapping configuration
const CUT_SNAP_MODE = 'seconds'; // 'none' | 'seconds' | 'frames'
const CUT_SNAP_FRAMES = 24; // When using 'frames' mode, snap to this many frames (24 frames = 1 second at 24fps)

/**
 * Core TimelineEditor - Generic, reusable timeline editing component
 *
 * @param {Object} props
 * @param {'sb_editor'|'rec'} props.mode - Timeline mode
 * @param {string} props.videoUrl - Video preview URL
 * @param {Track[]} props.tracks - Array of audio tracks
 * @param {string[]} [props.layers] - Fixed layer IDs (optional, for sb_editor mode)
 * @param {Function} props.getTrackVolume - Calculate volume for a track (0-1)
 * @param {Function} props.layerColors - Get colors for a layer
 * @param {boolean} [props.isPlayingExternal] - Controlled playback state
 * @param {number} [props.currentTimeExternal] - Controlled time position
 * @param {Function} [props.onPlaybackChange] - Playback state change callback
 * @param {Function} [props.onTimeChange] - Time change callback
 * @param {Function} [props.onDurationChange] - Timeline duration change callback
 * @param {Function} [props.onTracksChange] - Tracks updated callback
 * @param {Function} [props.onLayersChange] - Layers updated callback
 * @param {Function} [props.onTrackSelect] - Track selected callback
 * @param {Function} [props.onVideoUpload] - Called with File[] when user drops/selects a video
 * @param {boolean} [props.hideVideo] - Hide the video container entirely (e.g. for rec sessions)
 * @param {number} [props.fps] - Frames per second
 * @param {number} [props.minTrackDuration] - Minimum track duration in seconds
 * @param {number} [props.snapFrames] - Snap interval in frames
 * @param {number} [props.adsorptionDistance] - Snap distance in pixels
 */
const TimelineEditor = ({
    mode = 'sb_editor', // 'sb_editor' | 'rec'
    videoUrl,
    tracks: initialTracks = [],
    layers: propLayers,

    // Injected dependencies
    getTrackVolume = () => 1.0,  // Default: full volume
    layerColors = () => ({ color: '#BCE5FF', bgColor: 'rgba(188, 229, 255, 0.60)' }),

    // Controlled playback
    isPlayingExternal,
    currentTimeExternal,
    timelineEditingMode = '',
    onPlaybackChange,
    onTimeChange,
    onDurationChange,

    // Callbacks
    onTracksChange,
    onCut,
    onLayersChange,
    onTrackSelect,
    onVideoUpload,
    onExternalDrop,
    onLayerSelectionComplete,
    generateInstrumentLayer = null,
    onGenerateLayerSelectionComplete,
    hideVideo = false,
    videoPoster = null,

    // Undo/Redo
    undo,
    redo,
    canUndo = false,
    canRedo = false,
    undoLabel = null,
    redoLabel = null,

    // Per-layer height overrides: { [layerName]: heightInPx }
    layerHeightMap = {},

    // Per-layer gap after each layer (px): { [layerName]: gapInPx }
    layerGapMap = {},

    // Configuration
    fps = 24,
    minTrackDuration = 2,
    snapFrames = 12,
    adsorptionDistance = 10,
}) => {
    // Control video visibility based on UI collapsed state
    const timelineEditorUiCollapsed = useStoreValue('timelineEditorUiCollapsed');

    // Calculate effective layer height: 2x when video hidden, normal when visible
    const effectiveLayerHeight = timelineEditorUiCollapsed ? LAYER_HEIGHT * 2 : LAYER_HEIGHT;

    const [zoom, setZoom] = useState(100);

    // Internal layers state - use prop if provided
    const [internalLayers, setInternalLayers] = useState(
        mode === 'rec'
            ? ['layer-1', 'layer-2', 'layer-3'] // Start with 3 unlabeled layers
            : ['MUSIC', 'SFX', 'MIX'] // Fixed colored layers (uppercase to match API cueType)
    );

    const layers = propLayers || internalLayers;

    // Returns the height for a given layer, falling back to effectiveLayerHeight
    const getLayerHeight = useCallback((layerName) =>
        layerHeightMap[layerName] ?? effectiveLayerHeight,
    [layerHeightMap, effectiveLayerHeight]);

    const getLayerGap = useCallback((layerName) =>
        layerGapMap[layerName] ?? 0,
    [layerGapMap]);

    // Returns the cumulative Y offset for the layer at layerIndex
    const getLayerOffset = useCallback((layerIndex) =>
        layers.slice(0, layerIndex).reduce((sum, l) => sum + getLayerHeight(l) + getLayerGap(l), 0),
    [layers, getLayerHeight, getLayerGap]);

    // Playback state - use external if provided, otherwise use internal
    const [internalIsPlaying, setInternalIsPlaying] = useState(false);
    const [internalCurrentTime, setInternalCurrentTime] = useState(0);

    const isPlaying = isPlayingExternal !== undefined ? isPlayingExternal : internalIsPlaying;
    const currentTime = (currentTimeExternal !== undefined && currentTimeExternal !== null)
        ? currentTimeExternal
        : internalCurrentTime;

    const setIsPlaying = useCallback((playing) => {
        setInternalIsPlaying(playing);
        onPlaybackChange?.(playing);
    }, [onPlaybackChange]);

    const setCurrentTime = useCallback((time) => {
        setInternalCurrentTime(time);
        onTimeChange?.(time);
    }, [onTimeChange]);

    // Watch for external currentTime changes (for stop/backward/forward controls)
    useEffect(() => {
        if (currentTimeExternal !== undefined && currentTimeExternal !== null) {
            setInternalCurrentTime(currentTimeExternal);
            if (videoRef.current) {
                videoRef.current.currentTime = currentTimeExternal;
            }
        }
    }, [currentTimeExternal]);

    const [activeFrame, setActiveFrame] = useState(0);
    const [videoDuration, setVideoDuration] = useState(0);
    const [timelineDuration, setTimelineDuration] = useState(10);
    const [scrollX, setScrollX] = useState(0);
    const [totalTimelineWidth, setTotalTimelineWidth] = useState(0);
    const [containerWidth, setContainerWidth] = useState(0);
    const [tracks, setTracks] = useState(
        [...initialTracks]
            .sort((a, b) => (a.priority ?? 0) - (b.priority ?? 0))
            .map(track => ({
                ...track,
                start: track.startPosition || track.start || 0,
                audioUrl: track.url || track.audioUrl,
                actualDuration: track.actualDuration || track.duration,
                displayDuration: track.actualDuration ? track.actualDuration : track.duration,
                startPlayback: track.startPlayback || 0,
                waveform: [],
                audioBuffer: null,
                wavesurfer: null
            }))
    );

    // Sync tracks when initialTracks prop changes (e.g., after audio caching completes)
    useEffect(() => {
        setTracks(prevTracks => {
            // Create a map of existing tracks by ID for quick lookup
            const existingTracksMap = new Map(prevTracks.map(t => [t.id, t]));

            return [...initialTracks]
                .sort((a, b) => (a.priority ?? 0) - (b.priority ?? 0))
                .map(incomingTrack => {
                const existingTrack = existingTracksMap.get(incomingTrack.id);

                // If track already exists, merge updates preserving critical state
                if (existingTrack) {
                    // Check if audioUrl changed (e.g., from server URL to cached blob URL)
                    const audioUrlChanged = existingTrack.audioUrl !== (incomingTrack.url || incomingTrack.audioUrl);

                    return {
                        ...existingTrack,
                        // Update mutable position/duration fields so cut/move/undo changes take effect
                        start: incomingTrack.start ?? existingTrack.start,
                        duration: incomingTrack.duration ?? existingTrack.duration,
                        startPlayback: incomingTrack.startPlayback ?? existingTrack.startPlayback,
                        timecode: incomingTrack.timecode ?? existingTrack.timecode,
                        instanceDuration: incomingTrack.instanceDuration ?? existingTrack.instanceDuration,
                        // Update audioUrl if it changed (cached version now available)
                        audioUrl: incomingTrack.url || incomingTrack.audioUrl,
                        // Update other properties that may have changed server-side
                        filename: incomingTrack.filename || existingTrack.filename,
                        title: incomingTrack.title || existingTrack.title,
                        // If audioUrl changed and we have a WaveSurfer instance, mark for reload
                        ...(audioUrlChanged && existingTrack.wavesurfer ? { needsReload: true } : {}),
                    };
                }

                // New track - normalize as in initial useState
                return {
                    ...incomingTrack,
                    start: incomingTrack.startPosition || incomingTrack.start || 0,
                    audioUrl: incomingTrack.url || incomingTrack.audioUrl,
                    actualDuration: incomingTrack.actualDuration || incomingTrack.duration,
                    displayDuration: incomingTrack.actualDuration ? incomingTrack.actualDuration : incomingTrack.duration,
                    startPlayback: incomingTrack.startPlayback || 0,
                    waveform: [],
                    audioBuffer: null,
                    wavesurfer: null
                };
            });
        });
    }, [initialTracks]);

    const containerRef = useRef(null);
    const videoRef = useRef(null);
    const audioContextRef = useRef(null);
    const audioSourcesRef = useRef([]);
    const animationFrameRef = useRef(null);
    const playStartTimeRef = useRef(null);
    const dragStateRef = useRef(null);
    const playheadDragRef = useRef(false);
    const wavesurferRefs = useRef({});
    const activeAudioTracksRef = useRef(new Set());
    const wasPlayingBeforeDragRef = useRef(false);
    const tracksRef = useRef(tracks);
    const getTrackVolumeRef = useRef(getTrackVolume);
    getTrackVolumeRef.current = getTrackVolume;
    const layersRef = useRef(layers);
    layersRef.current = layers;
    const testPlayTimeoutRef = useRef(null);
    const resumePlaybackTimeoutRef = useRef(null);
    const [hoveredTrack, setHoveredTrack] = useState(null);
    const [selectedTrackId, setSelectedTrackId] = useState(null);
    const selectedTrackIdRef = useRef(selectedTrackId);
    const isPlayingRef = useRef(false);
    const [originalTracksBeforeDrag, setOriginalTracksBeforeDrag] = useState(null);
    const [previewTracks, setPreviewTracks] = useState(null);
    const [cutModeCursorX, setCutModeCursorX] = useState(null);
    const [externalPreviewTrack, setExternalPreviewTrack] = useState(null);

    // Stem splitter selection drag
    const stemSplitterDragRef = useRef(null); // { layer, startTime }
    const [stemSplitterOverlay, setStemSplitterOverlay] = useState(null); // { layer, startTime, endTime }

    // Context menu state for overlap priority swapping
    const [contextMenu, setContextMenu] = useState(null); // { x, y, items: [{ track, label }] }

    const setSelectedTrack = useCallback((trackId) => {
        setSelectedTrackId(trackId);
        onTrackSelect?.(trackId);
    }, [onTrackSelect]);

    const selectedTrack = selectedTrackId;

    // Wrapper for setTracks that emits onChange callback
    const updateTracks = useCallback((tracksOrUpdater) => {
        const newTracks = typeof tracksOrUpdater === 'function'
            ? tracksOrUpdater(tracksRef.current)
            : tracksOrUpdater;
        setTracks(newTracks);
        onTracksChange?.(newTracks);
    }, [onTracksChange]);
    const updateTracksRef = useRef(updateTracks);
    updateTracksRef.current = updateTracks;

    // Wrapper for setLayers that emits onChange callback
    const updateLayers = useCallback((layersOrUpdater) => {
        if (propLayers) return; // Don't update if layers are controlled
        const newLayers = typeof layersOrUpdater === 'function'
            ? layersOrUpdater(layersRef.current)
            : layersOrUpdater;
        setInternalLayers(newLayers);
        onLayersChange?.(newLayers);
    }, [propLayers, onLayersChange]);

    // External drag-from-panel preview
    const computeExternalPreviewRef = useRef(null);
    computeExternalPreviewRef.current = (extState) => {
        if (!extState.isDragging || !containerRef.current) { setExternalPreviewTrack(null); return; }
        const rect = containerRef.current.getBoundingClientRect();
        const relX = extState.x - rect.left + scrollX;
        const relY = extState.y - rect.top;
        if (relX < 0 || relY < 0 || extState.x < rect.left || extState.x > rect.right
            || extState.y < rect.top || extState.y > rect.bottom) {
            setExternalPreviewTrack(null); return;
        }
        const targetLayer = extState.item.cueType;
        if (!layers.includes(targetLayer)) { setExternalPreviewTrack(null); return; }
        const targetStart = Math.max(0, (relX - TIMELINE_PADDING) / getPixelsPerSecond());
        setExternalPreviewTrack({
            ...extState.item,
            id: '__external_preview__',
            layer: targetLayer,
            start: targetStart,
            duration: (extState.item.instanceDuration || 5000) / 1000,
            priority: 0,
            isExternalPreview: true,
        });
    };

    useEffect(() => subscribeExternalDrag((s) => computeExternalPreviewRef.current?.(s)), []);

    // Clear stem splitter overlay when mode changes away from stemSplitter/generateInstrument
    useEffect(() => {
        if (timelineEditingMode !== 'stemSplitter' && timelineEditingMode !== 'generateInstrument') {
            setStemSplitterOverlay(null);
            stemSplitterDragRef.current = null;
        }
    }, [timelineEditingMode]);

    // Audio loading queue to prevent resource contention
    const audioLoadQueue = useRef([]);
    const isLoadingAudio = useRef(false);

    // Audio update throttling
    const lastAudioUpdateTime = useRef(0);
    const AUDIO_UPDATE_INTERVAL = 50; // Update every 50ms instead of 16ms (~60fps)

    const [comments, setComments] = useState([
        /*{
            id: 'comment-1',
            frame: 90,
            type: 'note',
            text: 'Sample comment'
        },
        {
            id: 'comment-2',
            frame: 180,
            type: 'warning',
            text: 'Another comment'
        }*/
    ]);

    const calculateTimelineDuration = useCallback(() => {
        const maxVideoTime = videoDuration;
        const maxTrackTime = tracks.length > 0 
            ? Math.max(...tracks.map(track => track.start + track.duration))
            : 0;
        return Math.max(maxVideoTime, maxTrackTime, 10);
    }, [tracks, videoDuration]);

    const getPixelsPerFrame = useCallback(() => {
        return BASE_PIXELS_PER_FRAME * (zoom / 100);
    }, [zoom]);

    const getPixelsPerSecond = useCallback(() => {
        return getPixelsPerFrame() * FPS;
    }, [getPixelsPerFrame]);

    useEffect(() => {
        const updateContainerSize = () => {
            if (containerRef.current) {
                const rect = containerRef.current.getBoundingClientRect();
                const newWidth = Math.floor(rect.width);
                if (newWidth > 0) {
                    setContainerWidth(newWidth);
                }
            }
        };

        updateContainerSize();
        window.addEventListener('resize', updateContainerSize);
        
        return () => {
            window.removeEventListener('resize', updateContainerSize);
        };
    }, []);

    useEffect(() => {
        const newDuration = calculateTimelineDuration();
        setTimelineDuration(newDuration);
        onDurationChange?.(newDuration);
        const totalFrames = newDuration * FPS;
        const newTotalWidth = TIMELINE_PADDING + (totalFrames * getPixelsPerFrame()) + TIMELINE_PADDING;
        setTotalTimelineWidth(newTotalWidth);
    }, [calculateTimelineDuration, getPixelsPerFrame, onDurationChange]);

    const processAudioQueue = useCallback(async () => {
        if (isLoadingAudio.current || audioLoadQueue.current.length === 0) {
            return;
        }

        isLoadingAudio.current = true;
        const nextTrack = audioLoadQueue.current.shift();

        try {
            await loadAudioFile(nextTrack);
        } catch (error) {
            console.error('Audio load error:', error);
        }

        isLoadingAudio.current = false;

        // Process next in queue
        if (audioLoadQueue.current.length > 0) {
            processAudioQueue();
        }
    }, []);

    const queueAudioLoad = useCallback((track) => {
        if (!audioLoadQueue.current.find(t => t.id === track.id)) {
            audioLoadQueue.current.push(track);
            processAudioQueue();
        }
    }, [processAudioQueue]);

    const loadAudioFile = async (track) => {
        try {
            const container = document.createElement('div');
            container.style.display = 'none';
            document.body.appendChild(container);

            const wavesurfer = WaveSurfer.create({
                container: container,
                waveColor: '#161C21',
                progressColor: '#161C21',
                height: getLayerHeight(track.layer) - 5,
                interact: false,
                normalize: false, // Disabled - reduces CPU usage during audio processing
                // Ensure proper audio output
                backend: 'WebAudio',
                mediaControls: false
            });

            await wavesurfer.load(track.audioUrl);

            // Test if the WaveSurfer instance can produce audio
            //console.log('WaveSurfer loaded for track:', track.id, 'duration:', wavesurfer.getDuration(), 'volume:', wavesurfer.getVolume());
            
            // Set initial volume to maximum, will be controlled by effective volume during playback
            wavesurfer.setVolume(1.0);
            //console.log('Volume set to:', wavesurfer.getVolume());
            
            const actualDuration = wavesurfer.getDuration();
            const peaks = wavesurfer.getDecodedData().getChannelData(0);
            const sampledPeaks = [];
            const sampleSize = Math.floor(peaks.length / 200);
            
            for (let i = 0; i < 200; i++) {
                let sum = 0;
                for (let j = 0; j < sampleSize; j++) {
                    const index = i * sampleSize + j;
                    if (index < peaks.length) {
                        sum += Math.abs(peaks[index]);
                    }
                }
                sampledPeaks.push(sum / sampleSize);
            }

            updateTracks(prev => prev.map(t => {
                if (t.id === track.id) {
                    // Preserve all existing track properties, only update audio-related ones
                    const updatedTrack = {
                        ...t,
                        actualDuration,
                        totalDuration: actualDuration, // Store for resize boundary
                        waveform: sampledPeaks,
                        wavesurfer,
                        startPlayback: t.startPlayback || 0
                    };
                    // Only update durations if this is the first load (no existing actualDuration)
                    if (!t.actualDuration) {
                        updatedTrack.displayDuration = actualDuration / t.speed;
                        updatedTrack.duration = actualDuration / t.speed;
                    }
                    //console.log('Updated track with WaveSurfer data:', updatedTrack.id, 'preserving user modifications');
                    return updatedTrack;
                }
                return t;
            }));

            wavesurferRefs.current[track.id] = wavesurfer;
        } catch (error) {
            //console.log('WaveSurfer loading error, falling back:', error);
            loadAudioWithWebAudio(track);
        }
    };

    // Destroy all WaveSurfer instances only on component unmount
    useEffect(() => {
        return () => {
            Object.values(wavesurferRefs.current).forEach(ws => {
                if (ws) {
                    try { ws.stop(); } catch (e) {}
                    try { ws.destroy(); } catch (e) {}
                }
            });
            wavesurferRefs.current = {};
        };
    }, []);

    useEffect(() => {
        if (!audioContextRef.current) {
            audioContextRef.current = new (window.AudioContext || window.webkitAudioContext)();
        }

        // Stop and destroy WaveSurfer instances for tracks that have been removed
        const currentIds = new Set(tracks.map(t => String(t.id)));
        Object.keys(wavesurferRefs.current).forEach(idStr => {
            if (!currentIds.has(idStr)) {
                const ws = wavesurferRefs.current[idStr];
                if (ws) {
                    try { ws.stop(); } catch (e) {}
                    try { ws.destroy(); } catch (e) {}
                }
                delete wavesurferRefs.current[idStr];
            }
        });

        tracks.forEach(track => {
            // Load audio if:
            // 1. Track has audioUrl AND doesn't have wavesurfer yet (initial load)
            // 2. Track has needsReload flag (audioUrl changed, need to reload with new source)
            const needsInitialLoad = track.audioUrl && !track.wavesurfer && !wavesurferRefs.current[track.id];
            const needsReload = track.needsReload && track.audioUrl;

            if (needsInitialLoad || needsReload) {
                if (needsReload) {
                    // Clean up old WaveSurfer instance before reloading
                    const existingWs = wavesurferRefs.current[track.id];
                    if (existingWs && existingWs.destroy) {
                        try {
                            existingWs.destroy();
                        } catch (e) {
                            console.warn('Error destroying WaveSurfer:', e);
                        }
                    }
                    wavesurferRefs.current[track.id] = null;
                }

                //console.log('Queueing audio for track:', track.id, needsReload ? '(reload)' : '(initial)');
                queueAudioLoad(track);

                // Clear needsReload flag after queuing
                if (needsReload) {
                    setTracks(prev => prev.map(t =>
                        t.id === track.id ? { ...t, needsReload: false } : t
                    ));
                }
            }
        });

        return () => {
            audioSourcesRef.current.forEach(source => {
                if (source && source.stop) {
                    try {
                        source.stop();
                    } catch (e) {}
                }
            });
            audioSourcesRef.current = [];
        };
    }, [tracks, queueAudioLoad]);

    const loadAudioWithWebAudio = async (track) => {
        try {
            // Log audio source for debugging cache usage
            if (process.env.NODE_ENV === 'development') {
                const isCached = track.audioUrl?.startsWith('blob:');
                console.log(`Loading audio (WebAudio fallback) for track ${track.id}:`, isCached ? 'CACHED (blob URL)' : 'SERVER URL', track.audioUrl);
            }

            const response = await fetch(track.audioUrl);
            const arrayBuffer = await response.arrayBuffer();
            const audioBuffer = await audioContextRef.current.decodeAudioData(arrayBuffer);

            const channelData = audioBuffer.getChannelData(0);
            const samples = 200;
            const blockSize = Math.floor(channelData.length / samples);
            const waveform = [];

            for (let i = 0; i < samples; i++) {
                let sum = 0;
                for (let j = 0; j < blockSize; j++) {
                    sum += Math.abs(channelData[i * blockSize + j]);
                }
                waveform.push(sum / blockSize);
            }

            updateTracks(prev => prev.map(t =>
                t.id === track.id
                    ? { ...t, waveform, audioBuffer }
                    : t
            ));
        } catch (error) {
            //console.log('Audio loading error:', error);
        }
    };

    useEffect(() => {
        setActiveFrame(timeToFrame(currentTime));
    }, [currentTime]);

    const handleVideoLoaded = () => {
        if (videoRef.current) {
            const duration = videoRef.current.duration;
            setVideoDuration(duration);
        }
    };

    const handleVideoCanPlay = () => {
        // Video has buffered enough to start playing
        //console.log('Video ready to play');
    };

    const handleVideoWaiting = () => {
        // Video is waiting for data (buffering)
        //console.log('Video buffering...');
    };

    const handleVideoPlaying = () => {
        // Video resumed after buffering
        //console.log('Video playing');
    };

    const handleVideoProgress = () => {
        // Download progress - can show buffered ranges
        if (videoRef.current) {
            const buffered = videoRef.current.buffered;
            if (buffered.length > 0) {
                const bufferedEnd = buffered.end(buffered.length - 1);
                const duration = videoRef.current.duration;
                const percentBuffered = (bufferedEnd / duration) * 100;
                //console.log(`Video buffered: ${percentBuffered.toFixed(1)}%`);
            }
        }
    };

    const timeToPixels = useCallback((seconds) => {
        return TIMELINE_PADDING + (seconds * getPixelsPerSecond()) - scrollX;
    }, [getPixelsPerSecond, scrollX]);

    const pixelsToTime = useCallback((pixels) => {
        return (pixels + scrollX - TIMELINE_PADDING) / getPixelsPerSecond();
    }, [getPixelsPerSecond, scrollX]);

    const timeToFrame = (seconds) => {
        return Math.floor(seconds * FPS);
    };

    const getSnapInterval = useCallback(() => {
        const snapFrames = zoom >= 100 ? BASE_SNAP_FRAMES : BASE_SNAP_FRAMES * 2;
        return snapFrames / FPS;
    }, [zoom]);

    const snapToGrid = useCallback((value) => {
        const snapInterval = getSnapInterval();
        return Math.round(value / snapInterval) * snapInterval;
    }, [getSnapInterval]);

    const formatTime = (seconds) => {
        const mins = Math.floor(seconds / 60);
        const secs = Math.floor(seconds % 60);
        const ms = Math.floor((seconds % 1) * 100);
        return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
    };



    const getLayerFromMousePosition = useCallback((mouseY) => {
        // Calculate which layer based on mouse Y position
        // Must account for: tracks container offset (60px from CSS) + FIRST_TRACK_PADDING (2px)
        const adjustedY = mouseY - TRACKS_SECTION_OFFSET - FIRST_TRACK_PADDING;

        // Walk layers cumulatively to support variable heights and gaps
        let layerIndex = 0;
        let cumulative = 0;
        for (let i = 0; i < layers.length; i++) {
            cumulative += getLayerHeight(layers[i]) + getLayerGap(layers[i]);
            if (adjustedY < cumulative) {
                layerIndex = i;
                break;
            }
            layerIndex = i;
        }

        if (mode === 'sb_editor') {
            // Fixed 3 layers mode — use actual layers array (avoids hardcoded lowercase)
            const clampedIndex = Math.max(0, Math.min(layers.length - 1, layerIndex));
            return layers[clampedIndex];
        } else {
            // Rec mode - support automatic layer creation
            if (adjustedY < 0) {
                return '__CREATE_ABOVE__';
            }
            if (adjustedY >= cumulative) {
                return '__CREATE_BELOW__';
            }
            return layers[layerIndex];
        }
    }, [mode, layers, getLayerHeight, getLayerGap]);

    /**
     * Snap cut position based on CUT_SNAP_MODE configuration
     * @param {number} time - Time in seconds
     * @returns {number} Snapped time in seconds
     */
    const snapCutPosition = useCallback((time) => {
        if (CUT_SNAP_MODE === 'none') {
            return time;
        } else if (CUT_SNAP_MODE === 'seconds') {
            // Round to nearest whole second
            return Math.round(time);
        } else if (CUT_SNAP_MODE === 'frames') {
            // Round to nearest frame interval
            const frameTime = 1 / FPS; // Duration of one frame
            const snapInterval = (CUT_SNAP_FRAMES * frameTime); // Time interval for snapping
            return Math.round(time / snapInterval) * snapInterval;
        }
        return time;
    }, []);

    const createPreviewTracks = useCallback((draggedTrack, targetLayer, newStart) => {
        if (!originalTracksBeforeDrag) return tracks;
        
        return originalTracksBeforeDrag.map(track => {
            if (track.id === draggedTrack.id) {
                return { ...track, layer: targetLayer, start: newStart };
            }
            return track;
        });
    }, [originalTracksBeforeDrag, tracks]);


    const shiftTracksRight = useCallback((trackId, targetLayer, newStart, newDuration, allTracks) => {
        const newEnd = newStart + newDuration;
        const tracksInLayer = allTracks.filter(track => track.id !== trackId && track.layer === targetLayer);
        
        const overlappingTracks = tracksInLayer.filter(track => {
            const trackEnd = track.start + track.duration;
            return track.start < newEnd && trackEnd > newStart;
        });
        
        if (overlappingTracks.length === 0) return allTracks;
        
        // Sort by start time
        overlappingTracks.sort((a, b) => a.start - b.start);
        
        let shiftDistance = newEnd - overlappingTracks[0].start;
        
        return allTracks.map(track => {
            if (overlappingTracks.some(ot => ot.id === track.id)) {
                return {
                    ...track,
                    start: track.start + shiftDistance
                };
            }
            return track;
        });
    }, []);

    const getTimeInterval = useCallback(() => {
        if (zoom >= 150) return 1;
        if (zoom >= 100) return 5;
        return 10;
    }, [zoom]);

    const getSubInterval = useCallback(() => {
        const snapInterval = getSnapInterval();
        return snapInterval;
    }, [getSnapInterval]);

    const checkTrackOverlap = useCallback((trackId, layer, newStart, newDuration, allTracks) => {
        const newEnd = newStart + newDuration;
        
        return allTracks.some(track => {
            if (track.id === trackId || track.layer !== layer) return false;
            
            const trackEnd = track.start + track.duration;
            
            // Check for overlap: track overlaps if it starts before the new track ends and ends after the new track starts
            return track.start < newEnd && trackEnd > newStart;
        });
    }, []);

    const findValidPosition = useCallback((trackId, layer, preferredStart, duration, allTracks) => {
        const tracksInLayer = allTracks.filter(track => track.id !== trackId && track.layer === layer);
        
        // If no other tracks in layer, use preferred position
        if (tracksInLayer.length === 0) {
            return Math.max(0, preferredStart);
        }
        
        // Sort tracks by start time
        tracksInLayer.sort((a, b) => a.start - b.start);
        
        // Check if preferred position works
        if (!checkTrackOverlap(trackId, layer, preferredStart, duration, allTracks)) {
            return Math.max(0, preferredStart);
        }
        
        // Find the closest valid position to the preferred start
        let bestPosition = null;
        let minDistance = Infinity;
        
        // Check position before first track
        const beforeFirstEnd = Math.max(0, Math.min(preferredStart, tracksInLayer[0].start - duration));
        if (beforeFirstEnd >= 0 && beforeFirstEnd + duration <= tracksInLayer[0].start) {
            const distance = Math.abs(beforeFirstEnd - preferredStart);
            if (distance < minDistance) {
                minDistance = distance;
                bestPosition = beforeFirstEnd;
            }
        }
        
        // Check positions between tracks and adjacent to existing tracks
        for (let i = 0; i < tracksInLayer.length; i++) {
            const currentTrack = tracksInLayer[i];
            
            // Position immediately after current track (adjacent)
            const adjacentPosition = currentTrack.start + currentTrack.duration;
            const nextTrack = tracksInLayer[i + 1];
            const canFitAdjacent = !nextTrack || adjacentPosition + duration <= nextTrack.start;
            
            if (canFitAdjacent) {
                const distance = Math.abs(adjacentPosition - preferredStart);
                if (distance < minDistance) {
                    minDistance = distance;
                    bestPosition = adjacentPosition;
                }
            }
            
            // Position immediately before current track (if there's space)
            if (i === 0 || tracksInLayer[i - 1].start + tracksInLayer[i - 1].duration <= currentTrack.start - duration) {
                const beforePosition = Math.max(
                    i === 0 ? 0 : tracksInLayer[i - 1].start + tracksInLayer[i - 1].duration,
                    currentTrack.start - duration
                );
                if (beforePosition >= 0) {
                    const distance = Math.abs(beforePosition - preferredStart);
                    if (distance < minDistance) {
                        minDistance = distance;
                        bestPosition = beforePosition;
                    }
                }
            }
        }
        
        // Position after last track as fallback
        const lastTrack = tracksInLayer[tracksInLayer.length - 1];
        const afterLastPosition = lastTrack.start + lastTrack.duration;
        if (bestPosition === null) {
            bestPosition = afterLastPosition;
        } else {
            const distance = Math.abs(afterLastPosition - preferredStart);
            if (distance < minDistance) {
                bestPosition = afterLastPosition;
            }
        }
        
        return Math.max(0, bestPosition);
    }, [checkTrackOverlap]);

    const updateAudioTracks = useCallback(async (currentPlayTime) => {
        const currentlyActive = new Set();
        
        // Process tracks sequentially to handle async operations properly
        for (const track of tracksRef.current) {
            // Skip tracks whose layer is not currently active in the timeline
            if (layersRef.current && !layersRef.current.includes(track.layer)) continue;

            const trackEndTime = track.start + track.duration;
            const shouldPlay = currentPlayTime >= track.start && currentPlayTime < trackEndTime;
            
            if (shouldPlay) {
                currentlyActive.add(track.id);

                if (activeAudioTracksRef.current.has(track.id)) {
                    // Already playing — re-evaluate volume in case a new overlapping track entered/exited
                    if (track.wavesurfer) {
                        const baseVolume = getTrackVolumeRef.current(track);
                        const effectiveVolume = getTrackVolumeAtTime(track, tracksRef.current, currentPlayTime, baseVolume);
                        track.wavesurfer.setVolume(effectiveVolume);
                    }
                } else {
                    // If track wasn't active before, start it
                    // Debug logging
                    /*console.log('Attempting to play track:', track.id, 'at time:', currentPlayTime, 'track data:', {
                        hasWavesurfer: !!track.wavesurfer,
                        hasAudioBuffer: !!track.audioBuffer,
                        audioUrl: track.audioUrl,
                        start: track.start,
                        duration: track.displayDuration
                    });*/
                    
                    if (track.wavesurfer) {
                        try {
                            // Apply effective volume based on layer and general volume,
                            // reduced to 0 if a higher-priority track overlaps at current time
                            const baseVolume = getTrackVolumeRef.current(track);
                            const effectiveVolume = getTrackVolumeAtTime(track, tracksRef.current, currentPlayTime, baseVolume);
                            track.wavesurfer.setVolume(effectiveVolume);
                            
                            // Don't restart if already playing - just continue
                            if (track.wavesurfer.isPlaying()) {
                                //console.log('Track already playing, continuing:', track.id, 'volume:', effectiveVolume);
                            } else {
                                //console.log('Setting up WaveSurfer playback for track:', track.id, 'speed:', track.speed, 'volume:', effectiveVolume);
                                // track.wavesurfer.setPlaybackRate(track.speed);
                                // Calculate audio offset using startPlayback
                                const trackStartPlayback = track.startPlayback || 0;
                                const currentOffsetInTrack = currentPlayTime - track.start;
                                const audioTimeToSeek = trackStartPlayback + currentOffsetInTrack;
                                const audioEndTime = trackStartPlayback + track.duration;

                                // Ensure audio context is resumed before playing
                                if (audioContextRef.current && audioContextRef.current.state === 'suspended') {
                                    await audioContextRef.current.resume();
                                }

                                await track.wavesurfer.play(audioTimeToSeek, audioEndTime);
                                // Guard: if paused/stopped while play() was pending, immediately undo
                                if (!playStartTimeRef.current) {
                                    try { track.wavesurfer.pause(); } catch (_e) {}
                                    return;
                                }
                            }
                        } catch (error) {
                            //console.log('WaveSurfer playback error:', error);
                        }
                    } else if (track.audioBuffer) {
                        try {
                            const source = audioContextRef.current.createBufferSource();
                            const gainNode = audioContextRef.current.createGain();
                            
                            source.buffer = track.audioBuffer;
                            // source.playbackRate.value = track.speed;
                            source.loop = false;
                            
                            // Apply effective volume with overlap muting
                            const baseVolume = getTrackVolumeRef.current(track);
                            const effectiveVolume = getTrackVolumeAtTime(track, tracksRef.current, currentPlayTime, baseVolume);
                            gainNode.gain.value = effectiveVolume;

                            // Calculate audio offset using startPlayback
                            const trackStartPlayback = track.startPlayback || 0;
                            const currentOffsetInTrack = currentPlayTime - track.start;
                            const audioOffset = trackStartPlayback + currentOffsetInTrack;
                            // Cap at instanceDuration so the source stops at the track's visual end
                            const remainingInstanceDuration = (track.start + track.duration) - currentPlayTime;
                            const remainingAudioDuration = Math.min(track.actualDuration - audioOffset, remainingInstanceDuration);

                            source.connect(gainNode);
                            gainNode.connect(audioContextRef.current.destination);
                            source.start(0, audioOffset, remainingAudioDuration);
                            audioSourcesRef.current.push(source);
                            //console.log('Web Audio API play started for track:', track.id, 'offset:', audioOffset, 'duration:', remainingAudioDuration, 'volume:', effectiveVolume);
                        } catch (error) {
                            //console.log('Web Audio API playback error:', error);
                        }
                    } else {
                        console.log('Track has no audio data available:', track.id, 'audioUrl:', track.audioUrl);
                    }
                }
            }
        }
        
        // Stop tracks that are no longer active (use wavesurferRefs so removed tracks are also stopped)
        for (const trackId of activeAudioTracksRef.current) {
            if (!currentlyActive.has(trackId)) {
                const ws = wavesurferRefs.current[trackId];
                if (ws) {
                    try {
                        ws.stop();
                    } catch (error) {
                        console.log('Error stopping wavesurfer:', error);
                    }
                }
            }
        }
        
        // Update active tracks reference
        activeAudioTracksRef.current = currentlyActive;
    }, []);

    const handleTimelineClick = (e) => {
        if (timelineEditingMode !== 'arrow') return;

        // Use the fixed scroll container rect, not e.currentTarget (the scrolled content div).
        // e.currentTarget.getBoundingClientRect().left shifts left by scrollX as the content
        // scrolls, so subtracting it already produces a canvas coordinate. We want a
        // viewport-relative x so that pixelsToTime can correctly add scrollX once.
        const rect = containerRef.current.getBoundingClientRect();
        const viewportX = e.clientX - rect.left;
        const clickedTime = pixelsToTime(viewportX);
        const clampedTime = Math.max(0, Math.min(clickedTime, timelineDuration));

        setCurrentTime(clampedTime);
        setActiveFrame(timeToFrame(clampedTime));

        if (videoRef.current) {
            if (clampedTime <= videoDuration && videoDuration > 0) {
                videoRef.current.currentTime = clampedTime;
            } else if (videoDuration > 0) {
                videoRef.current.currentTime = videoDuration;
            }
        }
    };

    const handleCutClick = useCallback((e) => {
        if (timelineEditingMode !== 'cut') return;

        // Calculate click position in timeline time.
        // Use the fixed scroll container rect — same reason as handleTimelineClick.
        const rect = containerRef.current.getBoundingClientRect();
        const x = e.clientX - rect.left;
        const rawClickTime = pixelsToTime(x);

        // Snap cut position based on configuration
        const clickTime = snapCutPosition(rawClickTime);

        // Calculate which layer was clicked
        const y = e.clientY - rect.top;
        const clickedLayer = getLayerFromMousePosition(y);

        // Find track under cursor
        const clickedTrack = tracks.find(track => {
            const trackStart = track.start;
            const trackEnd = track.start + track.duration;
            const inTimeRange = clickTime >= trackStart && clickTime <= trackEnd;
            const inLayer = track.layer === clickedLayer;
            return inTimeRange && inLayer;
        });

        if (!clickedTrack) return;

        // Check if track is readOnly
        if (clickedTrack.readOnly) {
            console.warn('Cannot cut: track is read-only');
            return;
        }

        // Calculate cut position relative to track's start
        const cutPositionInTrack = clickTime - clickedTrack.start;

        // Enforce minimum duration constraint
        const remainingDuration = clickedTrack.duration - cutPositionInTrack;
        if (cutPositionInTrack < MIN_DURATION || remainingDuration < MIN_DURATION) {
            console.warn('Cannot cut: resulting tracks would be smaller than minimum duration');
            return;
        }

        // Validate audio bounds and adjust if needed
        const originalStartPlayback = clickedTrack.startPlayback || 0;
        const newRightStartPlayback = originalStartPlayback + cutPositionInTrack;
        let adjustedRemainingDuration = remainingDuration;

        // If the track extends beyond the actual audio, clamp the right segment duration
        const newRightEndTime = newRightStartPlayback + remainingDuration;
        if (newRightEndTime > clickedTrack.actualDuration) {
            adjustedRemainingDuration = Math.round((clickedTrack.actualDuration - newRightStartPlayback) * 1000) / 1000;

            // If the adjusted duration is too small, don't allow the cut
            if (adjustedRemainingDuration < MIN_DURATION) {
                console.warn('Cannot cut: adjusted right segment would be smaller than minimum duration');
                return;
            }
        }

        // Generate a unique ID for the new track
        const newTrackId = ulid();

        // Create updated tracks array
        const updatedTracks = tracks.map(track => {
            if (track.id === clickedTrack.id) {
                // Update original track (left side of cut)
                return {
                    ...track,
                    duration: Math.round(cutPositionInTrack * 1000) / 1000,
                    // startPosition and startPlayback remain unchanged
                };
            }
            return track;
        });

        // Add new track (right side of cut)
        const newTrack = {
            ...clickedTrack,
            id: newTrackId,
            start: clickedTrack.start + cutPositionInTrack,
            duration: adjustedRemainingDuration,
            startPlayback: newRightStartPlayback,
            // Force fresh audio loading - don't share WaveSurfer instances
            wavesurfer: null,
            audioBuffer: null,
            // Preserve waveform data (visual representation is safe to share)
        };

        updatedTracks.push(newTrack);

        // Stop the original WaveSurfer so playback restarts cleanly with the new shorter duration
        const existingWs = wavesurferRefs.current[clickedTrack.id];
        if (existingWs) {
            try { existingWs.stop(); } catch (e) {}
            activeAudioTracksRef.current.delete(clickedTrack.id);
        }

        // Update tracks state
        updateTracks(updatedTracks);

        // Notify parent about the cut for cuesheet-level persistence
        onCut?.({
            originalItem: clickedTrack,
            firstCutDuration: Math.round(cutPositionInTrack * 1000) / 1000,
            secondCutStart: clickedTrack.start + cutPositionInTrack,
            secondCutDuration: adjustedRemainingDuration,
            secondCutStartPlayback: newRightStartPlayback,
            newId: newTrackId,
        });

        // Select the new track
        setSelectedTrack(newTrackId);

        // Auto-disable cut mode after successful cut
        storeSet('timelineEditingMode', '');
    }, [timelineEditingMode, tracks, scrollX, pixelsToTime, snapCutPosition, getLayerFromMousePosition, updateTracks, setSelectedTrack, onCut]);

    const playTimeline = useCallback(() => {
        const safeCurrentTime = currentTime || 0;
        playStartTimeRef.current = performance.now() - (safeCurrentTime * 1000);

        // Resume AudioContext if suspended (required by browser autoplay policy)
        if (audioContextRef.current && audioContextRef.current.state === 'suspended') {
            audioContextRef.current.resume().catch(e => console.log('AudioContext resume error:', e));
        }

        // Clear active tracks when resuming so they can be properly restarted at current position
        //console.log('Starting/resuming playback, clearing active tracks for restart');
        activeAudioTracksRef.current.clear();

        if (videoRef.current) {
            if (safeCurrentTime <= videoDuration) {
                videoRef.current.currentTime = safeCurrentTime;
                videoRef.current.play().catch(e => console.log('Video play error:', e));
            } else {
                videoRef.current.currentTime = videoDuration;
                videoRef.current.pause();
            }
        }

        const updatePlayhead = () => {
            if (!playStartTimeRef.current) return;

            const elapsed = (performance.now() - playStartTimeRef.current) / 1000;
            const newTime = Math.min(elapsed, timelineDuration);

            setCurrentTime(newTime);
            setActiveFrame(timeToFrame(newTime));

            // Update video playback with improved sync
            if (videoRef.current && videoDuration > 0 && newTime <= videoDuration) {
                const videoTime = videoRef.current.currentTime;
                const drift = Math.abs(videoTime - newTime);

                // Only seek if drift is significant (>300ms) and video is buffered
                if (drift > 0.3) {
                    // Check if target time is buffered before seeking
                    const buffered = videoRef.current.buffered;
                    let isBuffered = false;

                    for (let i = 0; i < buffered.length; i++) {
                        if (newTime >= buffered.start(i) && newTime <= buffered.end(i)) {
                            isBuffered = true;
                            break;
                        }
                    }

                    if (isBuffered) {
                        videoRef.current.currentTime = newTime;
                    }
                }

                // Handle play/pause with error recovery
                if (videoRef.current.paused && playStartTimeRef.current) {
                    videoRef.current.play().catch(e => {
                        console.log('Video play error:', e);
                        // Retry once after short delay
                        setTimeout(() => {
                            if (videoRef.current && playStartTimeRef.current) {
                                videoRef.current.play().catch(() => {});
                            }
                        }, 100);
                    });
                }
            } else if (videoRef.current && videoDuration > 0 && newTime > videoDuration) {
                videoRef.current.currentTime = videoDuration;
                videoRef.current.pause();
            }

            // Throttle audio updates to ~20fps instead of 60fps
            const now = performance.now();
            if (now - lastAudioUpdateTime.current >= AUDIO_UPDATE_INTERVAL) {
                updateAudioTracks(newTime).catch(error => {
                    console.log('Error updating audio tracks:', error);
                });
                lastAudioUpdateTime.current = now;
            }

            if (newTime < timelineDuration && playStartTimeRef.current) {
                animationFrameRef.current = requestAnimationFrame(updatePlayhead);
            } else {
                setIsPlaying(false);
                playStartTimeRef.current = null;
                activeAudioTracksRef.current.clear();
            }
        };

        animationFrameRef.current = requestAnimationFrame(updatePlayhead);
    }, [currentTime, tracks, videoDuration, timelineDuration, updateAudioTracks]);

    const stopTimeline = useCallback(() => {
        setIsPlaying(false);
        setCurrentTime(0);
        setActiveFrame(0);

        audioSourcesRef.current.forEach(source => {
            if (source && source.stop) {
                try {
                    source.stop();
                } catch (e) {}
            }
        });
        audioSourcesRef.current = [];

        if (animationFrameRef.current) {
            cancelAnimationFrame(animationFrameRef.current);
            animationFrameRef.current = null; // Clear the ref!
        }
        playStartTimeRef.current = null;

        // Clear any pending timeout operations
        if (testPlayTimeoutRef.current) {
            clearTimeout(testPlayTimeoutRef.current);
            testPlayTimeoutRef.current = null;
        }
        if (resumePlaybackTimeoutRef.current) {
            clearTimeout(resumePlaybackTimeoutRef.current);
            resumePlaybackTimeoutRef.current = null;
        }

        if (videoRef.current) {
            videoRef.current.pause();
            videoRef.current.currentTime = 0;
        }

        // Stop ALL WaveSurfer tracks, not just active ones
        tracks.forEach(track => {
            if (track.wavesurfer) {
                try {
                    track.wavesurfer.stop();
                    // Reset to beginning
                    track.wavesurfer.seekTo(0);
                } catch (e) {
                    console.log('Error stopping wavesurfer track', track.id, ':', e);
                }
            }
        });

        // Also stop any WaveSurfer instances in the refs that might not be in current tracks
        Object.values(wavesurferRefs.current).forEach(ws => {
            if (ws) {
                try {
                    ws.stop();
                    ws.seekTo(0);
                } catch (e) {
                    console.log('Error stopping wavesurfer from refs:', e);
                }
            }
        });
        
        activeAudioTracksRef.current.clear();
    }, [tracks]);

    useEffect(() => {
        return () => {
            // CRITICAL: Call stopTimeline first for comprehensive cleanup
            stopTimeline();

            // Clear any pending timeouts that could fire after unmount
            if (testPlayTimeoutRef.current) {
                clearTimeout(testPlayTimeoutRef.current);
                testPlayTimeoutRef.current = null;
            }
            if (resumePlaybackTimeoutRef.current) {
                clearTimeout(resumePlaybackTimeoutRef.current);
                resumePlaybackTimeoutRef.current = null;
            }

            // Explicit video element cleanup (defense in depth)
            if (videoRef.current) {
                videoRef.current.pause();
                videoRef.current.currentTime = 0;
            }

            // Clean up animation frame (redundant with stopTimeline, but safe)
            if (animationFrameRef.current) {
                cancelAnimationFrame(animationFrameRef.current);
                animationFrameRef.current = null;
            }
            playStartTimeRef.current = null;

            // Stop all audio sources
            audioSourcesRef.current.forEach(source => {
                if (source && source.stop) {
                    try {
                        source.stop();
                    } catch (e) {}
                }
            });
            audioSourcesRef.current = [];

            // Destroy all WaveSurfer instances
            Object.values(wavesurferRefs.current).forEach(ws => {
                if (ws && ws.destroy) {
                    try {
                        ws.destroy();
                    } catch (e) {}
                }
            });
            wavesurferRefs.current = {};

            // Close AudioContext
            if (audioContextRef.current && audioContextRef.current.state !== 'closed') {
                try {
                    audioContextRef.current.close().then(() => {
                        audioContextRef.current = null;
                    }).catch(() => {
                        audioContextRef.current = null;
                    });
                } catch (e) {
                    audioContextRef.current = null;
                }
            }
        };
    }, [stopTimeline]);

    const moveToBeginning = useCallback(() => {
        setCurrentTime(0);
        setActiveFrame(0);
        if (videoRef.current) {
            videoRef.current.currentTime = 0;
        }
        if (isPlaying) {
            playStartTimeRef.current = performance.now();
        }
    }, [isPlaying]);

    const moveToEnd = useCallback(() => {
        setCurrentTime(timelineDuration);
        setActiveFrame(timeToFrame(timelineDuration));
        if (videoRef.current && videoDuration > 0) {
            videoRef.current.currentTime = Math.min(timelineDuration, videoDuration);
        }
        if (isPlaying) {
            playStartTimeRef.current = performance.now() - (timelineDuration * 1000);
        }
    }, [timelineDuration, videoDuration, isPlaying]);

    // Watch for isPlaying changes and trigger playback
    useEffect(() => {
        if (isPlaying && !animationFrameRef.current) {
            // Start playback
            playTimeline();
        } else if (!isPlaying) {
            // Stop playback (pause) — always run when not playing,
            // regardless of animation frame state
            audioSourcesRef.current.forEach(source => {
                if (source && source.stop) {
                    try {
                        source.stop();
                    } catch (e) {}
                }
            });
            audioSourcesRef.current = [];

            if (animationFrameRef.current) {
                cancelAnimationFrame(animationFrameRef.current);
                animationFrameRef.current = null; // Clear the ref!
            }
            playStartTimeRef.current = null;

            if (videoRef.current) {
                videoRef.current.pause();
            }

            // Pause via tracksRef (wavesurferRefs may be out of sync after re-renders)
            tracksRef.current.forEach(t => {
                if (!t.wavesurfer) return;
                try { t.wavesurfer.pause(); } catch (e) {}
            });
            // Also pause any instances in wavesurferRefs for completeness
            Object.values(wavesurferRefs.current).forEach(ws => {
                if (!ws) return;
                try { ws.pause(); } catch (e) {}
            });
            activeAudioTracksRef.current.clear();
        }
    }, [isPlaying, playTimeline, tracks]);

    // Pause video when hidden by Activity component
    useLayoutEffect(() => {
        return () => {
            // Cleanup when Activity mode changes to hidden
            if (videoRef.current) {
                videoRef.current.pause();
            }
        };
    }, [timelineEditorUiCollapsed]);

    // Watch for volume changes and update active tracks
    useEffect(() => {
        // Only update if we have active tracks during playback
        if (activeAudioTracksRef.current.size === 0) return;

        // Update volume for all currently playing tracks
        activeAudioTracksRef.current.forEach(trackId => {
            const track = tracks.find(t => t.id === trackId);
            if (track && track.wavesurfer) {
                const effectiveVolume = getTrackVolume(track);
                track.wavesurfer.setVolume(effectiveVolume);
            }
        });
    }, [getTrackVolume, tracks]);

    const renderTimeRuler = useCallback(() => {
        const mainInterval = getTimeInterval();
        const subInterval = getSubInterval();
        const elements = [];
        
        // Calculate the maximum time to render based on container width or timeline duration
        const maxDisplayWidth = Math.max(totalTimelineWidth, containerWidth);
        const maxDisplayTime = Math.max(timelineDuration, (maxDisplayWidth - 2 * TIMELINE_PADDING) / getPixelsPerSecond());

        for (let time = 0; time <= maxDisplayTime; time += subInterval) {
            const x = TIMELINE_PADDING + (time * getPixelsPerSecond());
            const isMainInterval = time % mainInterval === 0;
            
            // Only render if within the display width
            if (x <= maxDisplayWidth) {
                elements.push(
                    <div
                        key={`tick-${time}`}
                        className={`timeline-ruler__tick ${isMainInterval ? 'timeline-ruler__tick--major' : 'timeline-ruler__tick--minor'}`}
                        style={{
                            left: `${x}px`,
                            position: 'absolute',
                            top: isMainInterval ? `${COMMENT_SECTION_HEIGHT}px` : `${COMMENT_SECTION_HEIGHT + 20}px`,
                            height: isMainInterval ? '20px' : '10px'
                        }}
                    />
                );

                if (isMainInterval && time % 2 === 0) {
                    elements.push(
                        <div
                            key={`label-${time}`}
                            className="timeline-ruler__label"
                            style={{
                                left: `${x + 2}px`,
                                position: 'absolute',
                                top: '30px'
                            }}
                        >
                            {formatTime(time)}
                        </div>
                    );
                }
            }
        }

        return elements;
    }, [getTimeInterval, getSubInterval, timelineDuration, getPixelsPerSecond, totalTimelineWidth, containerWidth]);

    const renderGridLines = useCallback(() => {
        const mainInterval = getTimeInterval();
        const elements = [];
        const maxDisplayWidth = Math.max(totalTimelineWidth, containerWidth);
        const maxDisplayTime = Math.max(timelineDuration, (maxDisplayWidth - 2 * TIMELINE_PADDING) / getPixelsPerSecond());

        for (let time = 0; time <= maxDisplayTime; time += mainInterval) {
            const x = TIMELINE_PADDING + (time * getPixelsPerSecond());
            if (x <= maxDisplayWidth) {
                elements.push(
                    <div
                        key={`grid-${time}`}
                        className="timeline-grid__line"
                        style={{ left: `${x}px` }}
                    />
                );
            }
        }
        return elements;
    }, [getTimeInterval, timelineDuration, getPixelsPerSecond, totalTimelineWidth, containerWidth]);

    const renderComments = useCallback(() => {
        return comments.map(comment => {
            const frameTime = comment.frame / FPS;
            const x = TIMELINE_PADDING + (frameTime * getPixelsPerSecond());
            
            return (
                <div
                    key={comment.id}
                    className="timeline-comment"
                    style={{
                        position: 'absolute',
                        left: `${x - 6}px`,
                        top: '12px',
                        width: '0',
                        height: '0'
                    }}
                    title={comment.text}
                >
                    <div className="timeline-comment__line" />
                    <div className="timeline-comment__triangle" />
                </div>
            );
        });
    }, [comments, getPixelsPerSecond]);

    // Merged track list for rendering — includes external preview ghost when not internally dragging
    const displayTracks = useMemo(() => {
        const base = previewTracks || tracks;
        if (!externalPreviewTrack || dragStateRef.current) return base;
        return [...base, externalPreviewTrack];
    }, [previewTracks, tracks, externalPreviewTrack]);

    // Compute overlap map once per render for all active tracks
    const overlapMap = computeOverlaps(displayTracks);

    const handleTrackContextMenu = useCallback((e, track) => {
        e.preventDefault();
        e.stopPropagation();

        // viewport-relative x — pixelsToTime adds scrollX internally
        const clickTime = pixelsToTime(e.clientX - containerRef.current.getBoundingClientRect().left);

        // Find all tracks in the same layer that overlap at the exact click time
        const overlappingAtClick = (previewTracks || tracks).filter(t =>
            t.layer === track.layer &&
            clickTime >= t.start &&
            clickTime < t.start + t.duration
        );

        if (overlappingAtClick.length < 2) return;

        setContextMenu({
            x: e.clientX,
            y: e.clientY,
            sourceTrackId: track.id,
            items: overlappingAtClick
                .sort((a, b) => (b.priority ?? 0) - (a.priority ?? 0))
                .map(t => ({ id: t.id, label: t.title || t.filename || `Track ${t.id}`, priority: t.priority ?? 0 })),
        });
    }, [previewTracks, tracks, pixelsToTime, scrollX]);

    const handleContextMenuSwap = useCallback((targetId) => {
        const allTracks = previewTracks || tracks;

        // All overlapping tracks from the context menu, sorted by current priority ascending
        const overlapSorted = [...contextMenu.items]
            .sort((a, b) => (a.priority ?? 0) - (b.priority ?? 0));

        // Move the chosen track to the end (highest priority)
        const reordered = [
            ...overlapSorted.filter(item => item.id !== targetId),
            overlapSorted.find(item => item.id === targetId),
        ];

        // Build a map: id → new normalized priority (0, 1, 2, …)
        const priorityMap = new Map(reordered.map((item, i) => [item.id, i]));

        const updated = allTracks.map(t =>
            priorityMap.has(t.id) ? { ...t, priority: priorityMap.get(t.id) } : t
        );

        historyStore.getState().beginAction('Place on top');
        updateTracks(updated);
        setContextMenu(null);
    }, [contextMenu, previewTracks, tracks, updateTracks]);

    const renderTrackInLayer = (track) => {
        const x = TIMELINE_PADDING + (track.start * getPixelsPerSecond());
        const width = track.duration * getPixelsPerSecond();
        const height = getLayerHeight(track.layer) - 4;

        // External preview ghost — minimal, non-interactive rendering
        if (track.isExternalPreview) {
            const layerData = layerColors(track.layer);
            return (
                <div
                    key="__external_preview__"
                    className="timeline-track timeline-track--external-preview"
                    style={{
                        position: 'absolute',
                        left: `${x}px`,
                        top: '2px',
                        width: `${width}px`,
                        height: `${height}px`,
                        backgroundColor: layerData.bgColor,
                        color: layerData.color,
                        pointerEvents: 'none',
                        zIndex: 99,
                    }}
                >
                    <div className="timeline-track__info">
                        <div className="timeline-track__filename">{track.title || track.filename}</div>
                    </div>
                </div>
            );
        }

        const isHovered = track.id === hoveredTrack;
        const isSelected = track.id === selectedTrack;

        // Get colors for this layer
        const layerData = layerColors(track.layer);

        // Compute overlap stripe regions for this track (lower-priority ones show stripes)
        const allCurrentTracks = displayTracks;
        const overlappingIds = overlapMap.get(track.id) || [];
        const overlappingTracks = overlappingIds
            .map(id => allCurrentTracks.find(t => t.id === id))
            .filter(Boolean)
            .filter(other => (other.priority ?? 0) > (track.priority ?? 0)); // only higher-priority overlaps

        const overlapRegions = overlappingTracks.map(other => {
            const overlapStart = Math.max(track.start, other.start);
            const overlapEnd = Math.min(track.start + track.duration, other.start + other.duration);
            const regionLeft = (overlapStart - track.start) * getPixelsPerSecond();
            const regionWidth = (overlapEnd - overlapStart) * getPixelsPerSecond();
            return { key: other.id, left: regionLeft, width: regionWidth };
        });

        return (
            <div
                key={track.id}
                className={`timeline-track ${isSelected ? 'timeline-track--selected' : ''} ${isHovered ? 'timeline-track--hovered' : ''} ${track.readOnly ? 'timeline-track--readonly' : ''} ${!track.clip ? 'timeline-track--ghost' : ''}`}
                style={{
                    position: 'absolute',
                    left: `${x}px`,
                    top: '2px',
                    width: `${width}px`,
                    height: `${height}px`,
                    backgroundColor: layerData.bgColor,
                    color: layerData.color,
                    zIndex: (track.priority ?? 0) + 1,
                    cursor: track.readOnly
                        ? 'not-allowed'
                        : timelineEditingMode === 'cut'
                            ? 'crosshair'
                            : timelineEditingMode === 'arrow'
                                ? 'default'
                                : 'move'
                }}
                onMouseEnter={() => setHoveredTrack(track.id)}
                onMouseLeave={() => setHoveredTrack(null)}
                onMouseDown={(e) => handleTrackMouseDown(e, track)}
                onContextMenu={(e) => handleTrackContextMenu(e, track)}
            >
                {track.clip && (
                    <div className="timeline-track__waveform">
                        {track.waveform && (() => {
                            const fullWaveformWidth = (track.actualDuration || track.duration) * getPixelsPerSecond();
                            const waveformOffset = (track.startPlayback || 0) * getPixelsPerSecond();
                            return track.waveform.map((amplitude, i) => (
                                <div
                                    key={i}
                                    className="timeline-track__waveform-bar"
                                    style={{
                                        height: `${Math.abs(amplitude) * height * 2}px`,
                                        left: `${(i / track.waveform.length) * fullWaveformWidth - waveformOffset}px`,
                                        width: `${Math.max(1, fullWaveformWidth / track.waveform.length)}px`,
                                        backgroundColor: layerData.color,
                                        opacity: 0.8
                                    }}
                                />
                            ));
                        })()}
                    </div>
                )}

                {/* Diagonal stripe overlay where a higher-priority track covers this one */}
                {overlapRegions.map(region => (
                    <div
                        key={region.key}
                        className="timeline-track__overlap"
                        style={{
                            position: 'absolute',
                            top: 0,
                            left: `${region.left}px`,
                            width: `${region.width}px`,
                            height: '100%',
                        }}
                    />
                ))}

                <div className="timeline-track__info">
                    <div className="timeline-track__filename">
                        {track.filename.length > 20 ? track.filename.substring(0, 17) + '...' : track.filename}
                    </div>
                </div>

                {isSelected && !track.readOnly && (
                    <>
                        <div
                            className="timeline-track__handle timeline-track__handle--left"
                            onMouseDown={(e) => handleResizeMouseDown(e, track, 'left')}
                        />
                        <div
                            className="timeline-track__handle timeline-track__handle--right"
                            onMouseDown={(e) => handleResizeMouseDown(e, track, 'right')}
                        />
                    </>
                )}
            </div>
        );
    };

    const renderLayerContainer = (layerName, layerIndex) => {
        const layerTracks = displayTracks.filter(track => track.layer === layerName);
        const y = FIRST_TRACK_PADDING + getLayerOffset(layerIndex);

        const isGenerateLayer = layerName === generateInstrumentLayer;

        return (
            <div
                key={layerName}
                className={`timeline-editor__layer${isGenerateLayer ? ' timeline-editor__layer--generate-instrument' : ''}`}
                data-layer={layerName}
                style={{
                    position: 'absolute',
                    left: '0',
                    right: '0',
                    top: `${y}px`,
                    height: `${getLayerHeight(layerName)}px`,
                    pointerEvents: 'auto' // Enable pointer events for drop detection
                }}
            >
                {[...layerTracks].sort((a, b) => (a.priority ?? 0) - (b.priority ?? 0)).map(track => renderTrackInLayer(track))}
                {stemSplitterOverlay?.layer === layerName && (
                    <div
                        className="timeline-editor__stemSplitter-overlay"
                        style={{
                            left: `${TIMELINE_PADDING + stemSplitterOverlay.startTime * getPixelsPerSecond()}px`,
                            width: `${(stemSplitterOverlay.endTime - stemSplitterOverlay.startTime) * getPixelsPerSecond()}px`,
                        }}
                    />
                )}
                {timelineEditingMode === 'generateInstrument' && isGenerateLayer && (
                    <div
                        className="timeline-editor__generateInstrument-capture"
                        onMouseDown={(e) => handleGenerateInstrumentMouseDown(e, layerName)}
                    />
                )}
            </div>
        );
    };

    const renderTracks = () => {
        return (
            <>
                {layers.map((layerId, index) => renderLayerContainer(layerId, index))}
            </>
        );
    };

    const testPlayTrack = async (track) => {
        //console.log('Testing track playback:', track.id);
        
        // Resume AudioContext if needed
        if (audioContextRef.current && audioContextRef.current.state === 'suspended') {
            await audioContextRef.current.resume().catch(e => console.log('AudioContext resume error:', e));
        }
        
        if (track.wavesurfer) {
            try {
                //console.log('Testing WaveSurfer playback for track:', track.id);
                if (track.wavesurfer.isPlaying()) {
                    track.wavesurfer.stop();
                }
                
                // Apply effective volume for testing
                const effectiveVolume = getTrackVolume(track);
                track.wavesurfer.setVolume(effectiveVolume);
                
                track.wavesurfer.seekTo(0);
                track.wavesurfer.play();
                //console.log('WaveSurfer play started successfully with volume:', effectiveVolume);
            } catch (error) {
                console.log('WaveSurfer test play error:', error);
            }
        } else if (track.audioBuffer) {
            try {
                //console.log('Testing Web Audio API playback for track:', track.id);
                const source = audioContextRef.current.createBufferSource();
                const gainNode = audioContextRef.current.createGain();
                
                source.buffer = track.audioBuffer;
                
                // Apply effective volume for testing
                const effectiveVolume = getTrackVolume(track);
                gainNode.gain.value = effectiveVolume;
                
                source.connect(gainNode);
                gainNode.connect(audioContextRef.current.destination);
                source.start(0);
                //console.log('Web Audio API play started successfully with volume:', effectiveVolume);

                // Clear any existing test timeout
                if (testPlayTimeoutRef.current) {
                    clearTimeout(testPlayTimeoutRef.current);
                }

                // Store new timeout ID
                testPlayTimeoutRef.current = setTimeout(() => {
                    try {
                        source.stop();
                        //console.log('Test playback stopped');
                    } catch (e) {}
                    testPlayTimeoutRef.current = null;
                }, 2000);
            } catch (error) {
                console.log('Web Audio API test play error:', error);
            }
        } else {
            console.log('Track has no audio data for testing:', track.id);
        }
    };

    const handleResizeMouseDown = (e, track, direction) => {
        if (e.button !== 0) return;

        // Check readOnly flag
        if (track.readOnly) {
            e.stopPropagation();
            return;
        }

        // Disable resize in cut mode
        if (timelineEditingMode === 'cut') {
            e.stopPropagation();
            return;
        }

        // Disable resizing in arrow mode
        if (timelineEditingMode === 'arrow') {
            e.stopPropagation();
            return;
        }

        // Disable resizing in stemSplitter/generateInstrument mode
        if (timelineEditingMode === 'stemSplitter' || timelineEditingMode === 'generateInstrument') {
            e.stopPropagation();
            return;
        }

        e.stopPropagation();
        setSelectedTrack(track.id);

        // Auto-pause if playing
        if (isPlaying) {
            wasPlayingBeforeDragRef.current = true;
            setIsPlaying(false);
        }

        // Clear any pending resume to avoid conflicts
        if (resumePlaybackTimeoutRef.current) {
            clearTimeout(resumePlaybackTimeoutRef.current);
            resumePlaybackTimeoutRef.current = null;
        }

        const rect = containerRef.current.getBoundingClientRect();
        const x = e.clientX - rect.left + scrollX;

        // Store original track positions before starting resize
        setOriginalTracksBeforeDrag([...tracks]);
        setPreviewTracks(null);

        dragStateRef.current = {
            type: 'resize',
            direction: direction, // 'left' or 'right'
            trackId: track.id,
            startX: x,
            startY: e.clientY - rect.top,
            startTime: track.start,
            startDuration: track.duration,
            startPlayback: track.startPlayback || 0,
            maxDuration: track.totalDuration || track.actualDuration || track.duration,
            minDuration: MIN_DURATION,
            originalLayer: track.layer
        };
    };

    const handleStemSplitterMouseDown = (e) => {
        if (e.button !== 0) return;
        const rect = containerRef.current.getBoundingClientRect();
        const x = e.clientX - rect.left;
        const y = e.clientY - rect.top;
        const startTime = Math.max(0, pixelsToTime(x));
        const layer = getLayerFromMousePosition(y);
        stemSplitterDragRef.current = { layer, startTime };
        setStemSplitterOverlay({ layer, startTime, endTime: startTime });
    };

    const handleGenerateInstrumentMouseDown = (e, layerName) => {
        if (e.button !== 0) return;
        const rect = containerRef.current.getBoundingClientRect();
        const x = e.clientX - rect.left;
        const startTime = Math.max(0, pixelsToTime(x));
        stemSplitterDragRef.current = { layer: layerName, startTime };
        setStemSplitterOverlay({ layer: layerName, startTime, endTime: startTime });
    };

    const handleTrackMouseDown = (e, track) => {
        if (e.button !== 0) return;

        // Check readOnly flag
        if (track.readOnly) {
            setSelectedTrack(track.id);
            e.stopPropagation();
            return;
        }

        // In cut mode, allow event to bubble up to timeline-content onClick
        if (timelineEditingMode === 'cut') {
            setSelectedTrack(track.id);
            return;
        }

        // In stemSplitter/generateInstrument mode, let event bubble to capture overlay
        if (timelineEditingMode === 'stemSplitter' || timelineEditingMode === 'generateInstrument') {
            return;
        }

        // Disable dragging in arrow mode
        if (timelineEditingMode === 'arrow') {
            setSelectedTrack(track.id);
            e.stopPropagation();
            return;
        }

        e.stopPropagation();
        setSelectedTrack(track.id);

        // Auto-pause if playing
        if (isPlaying) {
            wasPlayingBeforeDragRef.current = true;
            setIsPlaying(false);
        }

        // Clear any pending resume to avoid conflicts
        if (resumePlaybackTimeoutRef.current) {
            clearTimeout(resumePlaybackTimeoutRef.current);
            resumePlaybackTimeoutRef.current = null;
        }

        const rect = containerRef.current.getBoundingClientRect();
        const x = e.clientX - rect.left + scrollX;
        const startY = e.clientY - rect.top;

        let dragType = 'move';

        // Store original track positions before starting drag
        setOriginalTracksBeforeDrag([...tracks]);
        setPreviewTracks(null);

        dragStateRef.current = {
            type: dragType,
            trackId: track.id,
            startX: x,
            startY: startY,
            startTime: track.start,
            startDuration: track.duration,
            originalLayer: track.layer
        };
    };

    const handleMouseMove = useCallback((e) => {
        // Handle stem splitter / generate instrument selection drag
        if (stemSplitterDragRef.current && (timelineEditingMode === 'stemSplitter' || timelineEditingMode === 'generateInstrument')) {
            const rect = containerRef.current.getBoundingClientRect();
            const x = e.clientX - rect.left;
            const rawTime = Math.max(0, pixelsToTime(x));
            const { layer, startTime } = stemSplitterDragRef.current;
            setStemSplitterOverlay({
                layer,
                startTime: Math.min(startTime, rawTime),
                endTime: Math.max(startTime, rawTime),
            });
            return;
        }

        // Track cursor position in cut mode with snapping
        if (timelineEditingMode === 'cut' && !playheadDragRef.current && !dragStateRef.current) {
            const rect = containerRef.current.getBoundingClientRect();
            const rawX = e.clientX - rect.left;

            // Convert to time, snap, then convert back to pixels for visual feedback
            // rawX is already viewport-relative; pixelsToTime adds scrollX internally
            const rawTime = pixelsToTime(rawX);
            const snappedTime = snapCutPosition(rawTime);
            const snappedX = TIMELINE_PADDING + (snappedTime * getPixelsPerSecond()) - scrollX;

            setCutModeCursorX(snappedX);
            return;
        }

        // Clear cut cursor during drag operations
        if (dragStateRef.current || playheadDragRef.current) {
            setCutModeCursorX(null);
        }

        if (playheadDragRef.current) {
            const rect = containerRef.current.getBoundingClientRect();
            // viewport-relative x — pixelsToTime adds scrollX internally
            const x = e.clientX - rect.left;
            const draggedTime = pixelsToTime(x);
            const clampedTime = Math.max(0, Math.min(draggedTime, timelineDuration));
            setCurrentTime(clampedTime);
            setActiveFrame(timeToFrame(clampedTime));

            if (videoRef.current) {
                if (clampedTime <= videoDuration && videoDuration > 0) {
                    videoRef.current.currentTime = clampedTime;
                } else if (videoDuration > 0) {
                    videoRef.current.currentTime = videoDuration;
                }
            }
            return;
        }

        // Add validation - check if drag state still exists
        if (!dragStateRef.current || !originalTracksBeforeDrag) {
            return;
        }

        const rect = containerRef.current.getBoundingClientRect();
        const x = e.clientX - rect.left + scrollX;
        const y = e.clientY - rect.top;
        const dragState = dragStateRef.current;
        const deltaX = x - dragState.startX;

        // Require minimum drag distance before generating preview
        if (Math.abs(deltaX) < DRAG_THRESHOLD) return;
        const deltaTime = deltaX / getPixelsPerSecond();

        // Get the current dragged track from original positions
        const draggedTrack = originalTracksBeforeDrag.find(t => t.id === dragState.trackId);
        if (!draggedTrack) return;

        if (dragState.type === 'resize') {
            // Handle resize logic
            let newStart = dragState.startTime;
            let newDuration = dragState.startDuration;

            if (dragState.direction === 'left') {
                // Left resize: change start, duration, and startPlayback
                const rightEdge = dragState.startTime + dragState.startDuration;
                newStart = Math.max(0, dragState.startTime + deltaTime);
                newDuration = rightEdge - newStart;

                // Apply constraints
                if (newDuration < dragState.minDuration) {
                    newStart = rightEdge - dragState.minDuration;
                    newDuration = dragState.minDuration;
                }
                if (newDuration > dragState.maxDuration) {
                    newStart = rightEdge - dragState.maxDuration;
                    newDuration = dragState.maxDuration;
                }

                // Apply grid snapping
                newStart = snapToGrid(newStart);
                newDuration = rightEdge - newStart;

                // Calculate how much time was trimmed from the left
                const trimmedTime = newStart - dragState.startTime;

                // Calculate new startPlayback
                const newStartPlayback = dragState.startPlayback + trimmedTime;

                // Create preview tracks with updated values
                const newPreviewTracks = originalTracksBeforeDrag.map(track => {
                    if (track.id === dragState.trackId) {
                        return {
                            ...track,
                            start: newStart,
                            duration: newDuration,
                            startPlayback: newStartPlayback
                        };
                    }
                    return track;
                });

                setPreviewTracks(newPreviewTracks);
            } else if (dragState.direction === 'right') {
                // Right resize: change duration only (keep left edge fixed)
                newDuration = Math.max(dragState.minDuration, dragState.startDuration + deltaTime);

                // Apply constraints
                if (newDuration > dragState.maxDuration) {
                    newDuration = dragState.maxDuration;
                }

                // Apply grid snapping to the right edge
                const rightEdge = snapToGrid(newStart + newDuration);
                newDuration = rightEdge - newStart;

                // Create preview tracks with updated values
                const newPreviewTracks = originalTracksBeforeDrag.map(track => {
                    if (track.id === dragState.trackId) {
                        return {
                            ...track,
                            start: newStart,
                            duration: newDuration
                        };
                    }
                    return track;
                });

                setPreviewTracks(newPreviewTracks);
            }
        } else {
            // Handle move logic
            let newStart = Math.max(0, snapToGrid(dragState.startTime + deltaTime));

            // Add playhead adsorption
            const currentTimePixels = TIMELINE_PADDING + (currentTime * getPixelsPerSecond());
            const newStartPixels = TIMELINE_PADDING + (newStart * getPixelsPerSecond());
            const draggedTrackEndPixels = newStartPixels + (draggedTrack.duration * getPixelsPerSecond());

            // Snap start edge to playhead
            if (Math.abs(newStartPixels - currentTimePixels) < ADSORPTION_DISTANCE) {
                newStart = currentTime;
            }
            // Snap end edge to playhead
            else if (Math.abs(draggedTrackEndPixels - currentTimePixels) < ADSORPTION_DISTANCE) {
                newStart = currentTime - draggedTrack.duration;
            }

            // Determine which layer mouse is over
            let targetLayer = getLayerFromMousePosition(y);

            // Handle automatic layer creation in rec mode
            if (mode === 'rec') {
                if (targetLayer === '__CREATE_ABOVE__') {
                    const newLayerId = `layer-${Date.now()}`;
                    updateLayers(prev => [newLayerId, ...prev]);
                    targetLayer = newLayerId;
                } else if (targetLayer === '__CREATE_BELOW__') {
                    const newLayerId = `layer-${Date.now()}`;
                    updateLayers(prev => [...prev, newLayerId]);
                    targetLayer = newLayerId;
                }
            }

            // Create preview tracks (this doesn't modify actual tracks, just the preview)
            const newPreviewTracks = createPreviewTracks(draggedTrack, targetLayer, newStart);
            setPreviewTracks(newPreviewTracks);
        }
    }, [scrollX, timelineDuration, videoDuration, pixelsToTime, timeToFrame, getPixelsPerSecond, snapCutPosition, snapToGrid, getLayerFromMousePosition, createPreviewTracks, originalTracksBeforeDrag, timelineEditingMode, setCutModeCursorX, currentTime, setStemSplitterOverlay]);

    const handleMouseUp = useCallback((e) => {
        // Handle stem splitter / generate instrument selection completion
        if (stemSplitterDragRef.current) {
            stemSplitterDragRef.current = null;
            if (stemSplitterOverlay && (stemSplitterOverlay.endTime - stemSplitterOverlay.startTime) > 0.05) {
                if (timelineEditingMode === 'generateInstrument') {
                    onGenerateLayerSelectionComplete?.({
                        layer: stemSplitterOverlay.layer,
                        startPos: stemSplitterOverlay.startTime,
                        endPos: stemSplitterOverlay.endTime,
                    });
                } else {
                    onLayerSelectionComplete?.({
                        layer: stemSplitterOverlay.layer,
                        startPos: stemSplitterOverlay.startTime,
                        endPos: stemSplitterOverlay.endTime,
                    });
                }
                // Keep overlay visible until modal is dismissed
            } else {
                setStemSplitterOverlay(null);
            }
            return;
        }

        // Handle drop from external panel (list → timeline)
        const extState = getExternalDragState();
        if (extState.isDragging) {
            setExternalPreviewTrack(null);
            if (containerRef.current && e) {
                const rect = containerRef.current.getBoundingClientRect();
                if (e.clientX >= rect.left && e.clientX <= rect.right
                    && e.clientY >= rect.top && e.clientY <= rect.bottom) {
                    const relX = e.clientX - rect.left + scrollX;
                    const targetLayer = extState.item.cueType;
                    if (layers.includes(targetLayer)) {
                        const targetStart = Math.max(0, (relX - TIMELINE_PADDING) / getPixelsPerSecond());
                        const timecode = secondsToTimecode(targetStart, FPS);
                        historyStore.getState().beginAction('Add to timeline');
                        onExternalDrop?.(extState.item, timecode);
                    }
                }
            }
            endExternalDrag();
            return;
        }

        // If we were dragging, commit the changes
        if (dragStateRef.current) {
            // If we have preview tracks, use them; otherwise keep current tracks
            if (previewTracks) {
                historyStore.getState().beginAction('Move');
                updateTracks(previewTracks);

                // After committing drag, check if any layers are empty (rec mode only)
                if (mode === 'rec' && layers.length > 1) {
                    const emptyLayers = layers.filter(layerId => {
                        return !previewTracks.some(track => track.layer === layerId);
                    });
                    if (emptyLayers.length > 0) {
                        updateLayers(prev => prev.filter(id => !emptyLayers.includes(id)));
                    }
                }
            } else {
                // Even if no preview (no mouse movement), the drag state existed
                // Just keep tracks as-is but clean up properly
            }
        }

        // Auto-resume if we paused automatically
        if (wasPlayingBeforeDragRef.current) {
            wasPlayingBeforeDragRef.current = false;

            // Clear any existing resume timeout
            if (resumePlaybackTimeoutRef.current) {
                clearTimeout(resumePlaybackTimeoutRef.current);
            }

            // Store new timeout ID
            resumePlaybackTimeoutRef.current = setTimeout(() => {
                setIsPlaying(true);
                resumePlaybackTimeoutRef.current = null;
            }, 10);
        }

        // Clean up drag state
        dragStateRef.current = null;
        playheadDragRef.current = false;
        setOriginalTracksBeforeDrag(null);
        setPreviewTracks(null);
    }, [previewTracks, mode, layers, scrollX, getPixelsPerSecond, onExternalDrop, stemSplitterOverlay, timelineEditingMode, onLayerSelectionComplete, onGenerateLayerSelectionComplete]);

    const handlePlayheadMouseDown = (e) => {
        e.stopPropagation();
        playheadDragRef.current = true;
    };

    // Use stable refs to prevent memory leaks and event listener conflicts
    const handleMouseMoveRef = useRef(handleMouseMove);
    const handleMouseUpRef = useRef(handleMouseUp);

    useEffect(() => {
        handleMouseMoveRef.current = handleMouseMove;
        handleMouseUpRef.current = handleMouseUp;
    }, [handleMouseMove, handleMouseUp]);

    useEffect(() => {
        const moveHandler = (e) => handleMouseMoveRef.current(e);
        const upHandler = (e) => handleMouseUpRef.current(e);

        document.addEventListener('mousemove', moveHandler);
        document.addEventListener('mouseup', upHandler);

        return () => {
            document.removeEventListener('mousemove', moveHandler);
            document.removeEventListener('mouseup', upHandler);
        };
    }, []); // Only runs once

    const handleScroll = (e) => {
        setScrollX(e.target.scrollLeft);
    };

    // Keep refs current so hotkey handlers can read latest values without re-binding
    useEffect(() => {
        tracksRef.current = tracks;
        selectedTrackIdRef.current = selectedTrackId;
        isPlayingRef.current = isPlaying;
    }, [tracks, selectedTrackId, isPlaying]);

    // Hotkeys: Delete/Backspace removes selected track from timeline; Space toggles play/pause
    useEffect(() => {
        hotkeys('delete,backspace', () => {
            const id = selectedTrackIdRef.current;
            if (!id) return;
            const track = tracksRef.current.find(t => t.id === id);
            if (!track || track.readOnly) return;
            // Stop audio immediately before the state update propagates to effects
            const ws = wavesurferRefs.current[id];
            if (ws) { try { ws.stop(); } catch (e) {} }
            activeAudioTracksRef.current.delete(id);
            historyStore.getState().beginAction('Delete');
            updateTracksRef.current(tracksRef.current.filter(t => t.id !== id));
            setSelectedTrack(null);
        });

        hotkeys('space', (e) => {
            e.preventDefault();
            setIsPlaying(!isPlayingRef.current);
        });

        return () => {
            hotkeys.unbind('delete,backspace');
            hotkeys.unbind('space');
        };
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    // Close context menu on outside click or ESC
    useEffect(() => {
        if (!contextMenu) return;
        const close = (e) => {
            if (e.type === 'keydown' && e.key !== 'Escape') return;
            setContextMenu(null);
        };
        document.addEventListener('click', close);
        document.addEventListener('keydown', close);
        return () => {
            document.removeEventListener('click', close);
            document.removeEventListener('keydown', close);
        };
    }, [contextMenu]);

    return (
        <>
        <div className="timeline-editor">
            <div className="timeline-editor__main">
                    {!hideVideo && (
                    <Activity mode={timelineEditorUiCollapsed ? 'hidden' : 'visible'}>
                        <div className="timeline-editor__video-container">
                            {videoUrl
                                ? <video
                                ref={videoRef}
                                src={videoUrl}
                                onLoadedMetadata={handleVideoLoaded}
                                onCanPlay={handleVideoCanPlay}
                                onWaiting={handleVideoWaiting}
                                onPlaying={handleVideoPlaying}
                                onProgress={handleVideoProgress}
                                className="timeline-editor__video"
                                controls={false}
                                preload="auto"
                                playsInline
                            />
                                : <div className="timeline-editor__videoPlaceholder">
                                    <FileuploadDropZone
                                        title={__('Carica video', 'soundbetween')}
                                        accept="video/mp4,.mp4"
                                        onFileSelect={onVideoUpload}
                                    />
                                </div>}
                            {videoPoster && videoUrl && !isPlaying && internalCurrentTime < 0.05 && (
                                <img
                                    src={videoPoster}
                                    alt=""
                                    className="timeline-editor__video-poster"
                                    aria-hidden="true"
                                />
                            )}
                        </div>
                    </Activity>
                    )}

                <div className="timeline-editor__bottom-wrapper">
                    <QuickActionsPanel
                        undo={undo}
                        redo={redo}
                        canUndo={canUndo}
                        canRedo={canRedo}
                        undoLabel={undoLabel}
                        redoLabel={redoLabel}
                    />
                    <div className="timeline-editor__timeline-wrapper">
                        <div
                            ref={containerRef}
                            className="timeline-editor__timeline-container"
                            style={{
                                overflowX: totalTimelineWidth > containerWidth ? 'auto' : 'hidden',
                                overflowY: 'hidden',
                                width: '100%',
                                position: 'relative',
                                border: '1px solid #2a2a2a'
                            }}
                            onScroll={handleScroll}
                        >
                            <div
                                className="timeline-editor__timeline-content"
                                style={{
                                    width: `${Math.max(totalTimelineWidth, containerWidth)}px`,
                                    minWidth: `${containerWidth}px`,
                                    height: '100%',
                                    position: 'relative',
                                    backgroundColor: '#161C21',
                                    cursor: timelineEditingMode === 'stemSplitter' ? 'crosshair'
                                        : timelineEditingMode === 'generateInstrument' ? 'crosshair'
                                        : timelineEditingMode === 'cut' ? 'crosshair'
                                        : timelineEditingMode === 'arrow' ? 'pointer'
                                        : 'default'
                                }}
                                onClick={
                                    timelineEditingMode === 'cut'
                                        ? handleCutClick
                                        : timelineEditingMode === 'arrow'
                                            ? handleTimelineClick
                                            : undefined
                                }
                            >
                                <div className="timeline-editor__header" onClick={timelineEditingMode === 'arrow' ? handleTimelineClick : undefined}>
                                    {renderTimeRuler()}
                                </div>

                                <div className="timeline-editor__comment-section">
                                    {renderComments()}
                                </div>

                                <div className="timeline-editor__grid">
                                    {renderGridLines()}
                                </div>

                                <div className="timeline-editor__tracks">
                                    {renderTracks()}
                                </div>

                                <div
                                    className="timeline-editor__playhead"
                                    style={{
                                        left: `${TIMELINE_PADDING + (currentTime * getPixelsPerSecond())}px`,
                                        position: 'absolute',
                                        top: `${COMMENT_SECTION_HEIGHT}px`,
                                        height: `calc(100% - ${COMMENT_SECTION_HEIGHT}px)`
                                    }}
                                    onMouseDown={handlePlayheadMouseDown}
                                >
                                    <div className="timeline-editor__playhead-line" />
                                </div>

                                {/* Stem splitter capture overlay */}
                                {timelineEditingMode === 'stemSplitter' && (
                                    <div
                                        className="timeline-editor__stemSplitter-capture"
                                        onMouseDown={handleStemSplitterMouseDown}
                                    />
                                )}

                                {/* Cut mode cursor line */}
                                {timelineEditingMode === 'cut' && cutModeCursorX !== null && (
                                    <div
                                        style={{
                                            position: 'absolute',
                                            left: `${cutModeCursorX + scrollX}px`,
                                            top: `${COMMENT_SECTION_HEIGHT}px`,
                                            height: `calc(100% - ${COMMENT_SECTION_HEIGHT}px)`,
                                            width: '1px',
                                            backgroundColor: 'var(--color-6)',
                                            pointerEvents: 'none',
                                            zIndex: 5
                                        }}
                                    />
                                )}
                            </div>
                        </div>

                        <div className="timeline-editor__controls">
                            <div className="timeline-editor__time-display">
                                {formatTime(currentTime)} / {formatTime(timelineDuration)}
                            </div>

                            <div className="timeline-editor__frame-display">
                                Frame: {activeFrame}
                            </div>

                            <div className="timeline-editor__zoom-controls">
                                <button
                                    onClick={() => setZoom(Math.max(25, zoom - 25))}
                                    className="timeline-editor__zoom-btn"
                                >
                                    −
                                </button>
                                <input
                                    type="range"
                                    min="25"
                                    max="150"
                                    step="25"
                                    value={zoom}
                                    onChange={(e) => setZoom(parseInt(e.target.value))}
                                    className="timeline-editor__zoom-slider"
                                />
                                <button
                                    onClick={() => setZoom(Math.min(150, zoom + 25))}
                                    className="timeline-editor__zoom-btn"
                                >
                                    +
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>

        {/* Overlap priority context menu */}
        {contextMenu && (
            <div
                className="timeline-track__contextMenu"
                style={{ position: 'fixed', top: contextMenu.y, left: contextMenu.x }}
                onMouseLeave={() => setContextMenu(null)}
            >
                <div className="timeline-track__contextMenu-header">Bring to front</div>
                <ul className="timeline-track__contextMenu-list">
                    {contextMenu.items.map(item => (
                        <li
                            key={item.id}
                            className={`timeline-track__contextMenu-item${item.id === contextMenu.sourceTrackId ? ' timeline-track__contextMenu-item--active' : ''}`}
                            onClick={() => handleContextMenuSwap(item.id)}
                        >
                            {item.label}
                        </li>
                    ))}
                </ul>
            </div>
        )}
        </>
    );
};

export default TimelineEditor;